import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { LoggingService } from 'shared/domain';

import { AuthService } from './auth.service';

let redirecting = false;
let lastRedirectTime = 0;
const REDIRECT_DEBOUNCE_MS = 1000;

export const authHttpInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authStorage = inject(OAuthStorage, { optional: true });
  const logger = inject(LoggingService);
  const router = inject(Router);

  let request$: Observable<HttpEvent<unknown>>;

  if (req.url.includes('/api/') && authStorage) {
    const authService = inject(AuthService);
    // Refresh an expired or expiring token first, then read it from storage, so the request carries the fresh one
    request$ = from(authService.ensureFreshAccessToken()).pipe(switchMap(() => next(addAccessTokenToRequest(req, authStorage))));
  } else {
    request$ = next(req);
  }

  return request$.pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        handleUnauthorized(req, router, logger);
      }
      return throwError(() => err);
    }),
  );
};

function handleUnauthorized(req: HttpRequest<unknown>, router: Router, logger: LoggingService) {
  if (req.url.includes('/api/users/init') || req.url.includes('/api/users/track-login')) {
    return;
  }

  const currentUrl = router.url;
  if (currentUrl.startsWith('/login')) return;

  const now = Date.now();
  if (redirecting || now - lastRedirectTime < REDIRECT_DEBOUNCE_MS) return;

  redirecting = true;
  lastRedirectTime = now;

  router.navigate(['/login']).then(
    () => {
      redirecting = false;
    },
    (error) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error(
        'AuthHttpInterceptor',
        `Failed to navigate to login: ${errorMessage}`,
        error instanceof Error ? error : new Error(errorMessage),
      );
      redirecting = false;
    },
  );
}

const addAccessTokenToRequest = (req: HttpRequest<any>, authStorage: OAuthStorage): HttpRequest<any> => {
  const token = authStorage.getItem('access_token');

  // Requests sent before login completes (e.g. feature flags, maintenance check) would otherwise carry "Bearer null",
  // which the API logs as a malformed JWT (IDX14100).
  if (!token) {
    return req;
  }

  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
};
